import React, {FC, useState} from 'react';
import {withAuthenticationRequired} from '@auth0/auth0-react';
import {useTranslation} from 'react-i18next';
import {Spin} from 'antd';
import {FaMagnifyingGlass} from "react-icons/fa6";
import {HiMiniWrenchScrewdriver} from "react-icons/hi2";

import {Enquiry} from '../model/enquiry';
import UserPermissions from '../model/userPermissions';

import {getEnquiries} from '../service/dao/EnquiryDao';

import LinkButton from '../component/LinkButton';
import EnquiryTable, {
  enquiryLinks,
  filterColumn,
  renderBookingStatus,
  renderBookingStatusUpdated,
  renderEvidenceProvider,
  renderEvidenceRequested,
  allStatuses,
} from '../component/EnquiryTable/EnquiryTable';
import RetryEnquiryDialog from './RetryEnquiryDialog/RetryEnquiryDialog';
import EnquirySummaryDialog from './EnquirySummaryDialog/EnquirySummaryDialog';

import './StatusTable.less';

const StatusTable: FC<{ permissions: UserPermissions, accessToken: string }> = ({ permissions, accessToken }) => {
  const [busy, setBusy] = useState(false);
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [currentEnquiry, setCurrentEnquiry] = useState<null | Enquiry>(null);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);

  const { t } = useTranslation();

  const openRetryDialog = (enquiry: Enquiry): void => {
    setCurrentEnquiry(enquiry);
    setRetryDialogOpen(true);
  };

  const onRetryDialogExit = (): void => {
    setRetryDialogOpen(false);
  };

  const openSummaryDialog = (enquiry: Enquiry): void => {
    setCurrentEnquiry(enquiry);
    setSummaryDialogOpen(true);
  };

  const onSummaryDialogExit = (): void => {
    setSummaryDialogOpen(false);
  };

  const renderRetryButton = (enquiry: Enquiry) => {
    return (
      <HiMiniWrenchScrewdriver
          className="action-button retry"
          title={`${t('status.table.retry.title')}`}
          onClick={(event) => {
            event.stopPropagation();
            openRetryDialog(enquiry);
          }}
      />
    );
  };

  const renderSummaryButton = (enquiry: Enquiry) => {
    return (
      <FaMagnifyingGlass
        className="action-button summary"
        title={`${t('status.table.summary.title')}`}
        onClick={(event) => {
          event.stopPropagation();
          openSummaryDialog(enquiry)
        }}
      />
    );
  };

  const columns: any[] = [
    {
      title: t('status.table.appid'),
      dataIndex: 'applicationId',
      render: (id: string, enquiry: Enquiry) => (<LinkButton text={id} url={enquiry.applicationUrl} maxWidth={180} />),
      sorter: true,
      ...filterColumn(t('status.table.appid'), 'applicationId'),
    },
    {
      title: t('status.table.name'),
      dataIndex: 'name',
      render: (_: any, enquiry: Enquiry) => `${enquiry.firstName} ${enquiry.surname}`,
      sorter: true,
      ...filterColumn(t('status.table.filter.title.name'), 'name'),
    },
    {
      title: t('status.table.last.updated'),
      dataIndex: 'bookingStatusUpdated',
      render: (_: any, enquiry: Enquiry) => (renderBookingStatusUpdated(enquiry.evidenceProviders)),
      sorter: true,
    },
    {
      title: t('status.table.evidence.requested'),
      dataIndex: 'evidenceRequested',
      render: (_: any, enquiry: Enquiry) => (renderEvidenceRequested(enquiry.evidenceProviders)),
      sorter: true,
      ...filterColumn(t('status.table.filter.title.evidence.request'), 'evidenceRequested'),
    },
    {
      title: t('status.table.evidence.provider'),
      dataIndex: 'evidenceProvider',
      render: (_: any, enquiry: Enquiry) => (renderEvidenceProvider(enquiry.evidenceProviders)),
      sorter: true,
      ...filterColumn(t('status.table.filter.title.evidence.provider'), 'evidenceProvider'),
    },
    {
      title: t('status.table.status'),
      dataIndex: 'bookingStatus',
      render: (_: any, enquiry: Enquiry) => (renderBookingStatus(enquiry.evidenceProviders)),
      sorter: true,
      ...filterColumn(t('status.table.filter.title.status'), 'bookingStatus', allStatuses),
    },
    {
      title: t('status.table.links'),
      dataIndex: 'parentEnquiryId',
      render: (id: string, enquiry: Enquiry) => enquiryLinks(accessToken, enquiry, t, 'status.table.enquiry.', setBusy),
    },
  ];

  // Retry and summary buttons only available to authorised users
  if (permissions.canRetryEnquiry || permissions.canSummaryEnquiry) {
    columns.push(
      {
        title: t('status.table.actions'),
        dataIndex: 'applicationId',
        className: 'action-button-cell',
        render: (_: any, enquiry: Enquiry) => {
          return (
            <>
              { permissions.canRetryEnquiry && renderRetryButton(enquiry) }
              { permissions.canSummaryEnquiry && renderSummaryButton(enquiry) }
            </>
          );
        }
      }
    );
  }

  return (
    <div className="status-table">
      <EnquiryTable columns={columns} getEnquiries={getEnquiries} expandable accessToken={accessToken} />
      <div className="spinner">
        <Spin spinning={busy} />
      </div>
      <RetryEnquiryDialog
          accessToken={accessToken}
          onExit={onRetryDialogExit}
          open={retryDialogOpen}
          permissions={permissions}
          enquiry={currentEnquiry}
      />
      <EnquirySummaryDialog
        accessToken={accessToken}
        onExit={onSummaryDialogExit}
        open={summaryDialogOpen}
        enquiryId={currentEnquiry?.enquiryId}
      />
    </div>
  );
};

export default withAuthenticationRequired(StatusTable, {});
